import { useEffect, useState, useRef } from 'react';
import type { Dispatch } from 'react';
import { useAppContext } from '../contexts/hooks';
import type { AppAction } from '../contexts/AppContext';
import { useAuthBootstrap } from '../hooks/useAuthBootstrap';
import { getBrokers } from '../services/orderly-api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import type { ChainType } from '../types';
import { trackEvent } from '../lib/analytics';
import { Card, CardContent } from '@/components/ui/card';
import { OdsAlert } from '@/components/orderly/OdsAlert';
import { OdsButton } from '@/components/orderly/OdsButton';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
type ToastVariant = 'warning' | 'error';

function isUserRejectedError(error: unknown): boolean {
  const code = error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
  const message = error instanceof Error ? error.message : String(error ?? '');
  const lowerMessage = message.toLowerCase();

  return (
    code === 'ACTION_REJECTED'
    || code === '4001'
    || lowerMessage.includes('action_rejected')
    || lowerMessage.includes('user rejected')
    || lowerMessage.includes('rejected')
  );
}

function useFetchBrokers(
  walletAddress: string | null,
  chain: ChainType | null,
  brokerCount: number,
  dispatch: Dispatch<AppAction>,
) {
  const requestSeqRef = useRef(0);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    requestSeqRef.current += 1;
    const requestId = requestSeqRef.current;

    if (USE_MOCK || !walletAddress || !chain || brokerCount > 0) {
      const resetTimer = window.setTimeout(() => {
        setFetchError(null);
        setFetching(false);
      }, 0);
      return () => window.clearTimeout(resetTimer);
    }

    const loadingTimer = window.setTimeout(() => setFetching(true), 0);

    getBrokers(walletAddress, chain === 'solana' ? 'SOL' : 'EVM')
      .then((data) => {
        if (requestSeqRef.current !== requestId) return;
        setFetchError(null);
        const brokers = data.map((b) => ({
          id: b.broker_id,
          name: b.broker_name || b.broker_id,
          accounts: [],
        }));
        dispatch({ type: 'SET_BROKERS', brokers, walletAddress, chain });
      })
      .catch((err) => {
        if (requestSeqRef.current !== requestId) return;
        setFetchError(err instanceof Error ? err.message : 'Failed to load DEXs');
      })
      .finally(() => {
        window.clearTimeout(loadingTimer);
        if (requestSeqRef.current === requestId) setFetching(false);
      });

    return () => {
      window.clearTimeout(loadingTimer);
      if (requestSeqRef.current === requestId) {
        requestSeqRef.current += 1;
        setFetching(false);
      }
    };
  }, [walletAddress, chain, brokerCount, dispatch, retryNonce]);

  return {
    fetching,
    fetchError,
    retry: () => {
      setFetchError(null);
      setFetching(true);
      setRetryNonce((value) => value + 1);
    },
  };
}

export default function SelectBroker() {
  const { state, dispatch } = useAppContext();
  const { bootstrap, progress: authProgress } = useAuthBootstrap();
  const { fetching, fetchError, retry } = useFetchBrokers(state.walletAddress, state.chain, state.brokers.length, dispatch);
  const [toast, setToast] = useState<{ message: string; variant: ToastVariant } | null>(null);
  const [pendingBrokerId, setPendingBrokerId] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const trackedDexListKeyRef = useRef<string | null>(null);
  const authInFlightRef = useRef(false);
  const authRequestSeqRef = useRef(0);
  const pendingBrokerIdRef = useRef<string | null>(null);
  const activeSessionRef = useRef({ walletAddress: state.walletAddress, chain: state.chain });

  useEffect(() => {
    activeSessionRef.current = { walletAddress: state.walletAddress, chain: state.chain };
    authRequestSeqRef.current += 1;
    authInFlightRef.current = false;
    pendingBrokerIdRef.current = null;

    const resetTimer = window.setTimeout(() => setPendingBrokerId(null), 0);
    return () => {
      window.clearTimeout(resetTimer);
      authRequestSeqRef.current += 1;
      authInFlightRef.current = false;
      pendingBrokerIdRef.current = null;
    };
  }, [state.walletAddress, state.chain]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'SelectBroker',
      page: 'select_broker',
      chain: state.chain ?? undefined,
      status: state.brokers.length,
    });
  }, [state.chain, state.brokers.length]);

  useEffect(() => {
    if (!state.walletAddress || !state.chain) return;
    const key = `${state.walletAddress}:${state.chain}`;
    if (trackedDexListKeyRef.current === key) return;

    trackEvent('dex_list_viewed', {
      chain: state.chain,
      dex_count: state.brokers.length,
    });
    trackedDexListKeyRef.current = key;
  }, [state.walletAddress, state.chain, state.brokers.length]);

  async function selectBroker(brokerId: string) {
    if (authInFlightRef.current) return;

    trackEvent('dex_selected', { broker_id: brokerId, chain: state.chain ?? undefined });

    if (USE_MOCK) {
      dispatch({ type: 'SELECT_BROKER', brokerId });
      return;
    }
    setToast(null);

    const walletAddress = state.walletAddress;
    const chain = state.chain;
    if (!walletAddress || !chain) {
      setToast({ message: 'Wallet not connected', variant: 'error' });
      return;
    }

    authInFlightRef.current = true;
    const requestId = authRequestSeqRef.current + 1;
    authRequestSeqRef.current = requestId;
    pendingBrokerIdRef.current = brokerId;
    setPendingBrokerId(brokerId);
    dispatch({ type: 'CLEAR_AUTH_CONTEXT' });

    const selectionSession = { walletAddress, chain, brokerId };
    const isCurrentSelection = () => (
      authInFlightRef.current
      && authRequestSeqRef.current === requestId
      && pendingBrokerIdRef.current === brokerId
      && activeSessionRef.current.walletAddress === selectionSession.walletAddress
      && activeSessionRef.current.chain === selectionSession.chain
    );

    try {
      await bootstrap(brokerId);
      if (!isCurrentSelection()) return;
      dispatch({ type: 'SELECT_BROKER', brokerId, walletAddress, chain });
    } catch (err) {
      if (!isCurrentSelection()) return;

      if (isUserRejectedError(err)) {
        setToast({
          message: 'You rejected the wallet action. Try again if needed.',
          variant: 'warning',
        });
        if (toastTimerRef.current) {
          window.clearTimeout(toastTimerRef.current);
        }
        toastTimerRef.current = window.setTimeout(() => {
          setToast(null);
          toastTimerRef.current = null;
        }, 3200);
        return;
      }

      const backendErrorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setToast({
        message: backendErrorMessage,
        variant: 'error',
      });
    } finally {
      if (authRequestSeqRef.current === requestId) {
        authInFlightRef.current = false;
        pendingBrokerIdRef.current = null;
        setPendingBrokerId(null);
      }
    }
  }

  if (fetching) return <LoadingSpinner message="Loading DEXs..." />;

  const isBootstrapping = pendingBrokerId !== null;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl text-[var(--fg-base)]">Select DEX</h2>
        <p className="mt-2 text-sm text-[var(--fg-secondary)]">Choose the DEX you want to offboard from.</p>
      </div>

      {toast && (
        <OdsAlert
          type={toast.variant === 'warning' ? 'warning' : 'negative'}
          className="fixed right-4 top-20 z-50 max-w-[min(440px,calc(100vw-24px))] shadow-[var(--shadow-popout)] max-sm:left-3 max-sm:right-3 max-sm:max-w-none"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="break-words text-sm text-[var(--fg-base)]">{toast.message}</div>
              <div className="mt-1 text-xs">{toast.variant === 'warning' ? 'Warning' : 'Error'}</div>
            </div>
            <OdsButton variant="secondary" size="small" className="shrink-0 px-3" onClick={() => setToast(null)} aria-label="Close">
              Close
            </OdsButton>
          </div>
        </OdsAlert>
      )}

      {fetchError && (
        <OdsAlert type="negative" role="alert" aria-live="assertive">
          <div className="flex items-center justify-between gap-3">
            <span>{fetchError}</span>
            <OdsButton size="small" variant="secondary" onClick={retry}>Retry</OdsButton>
          </div>
        </OdsAlert>
      )}

      {isBootstrapping ? (
        <LoadingSpinner message={authProgress || 'Authenticating...'} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {state.brokers.map((broker) => (
            <button
              key={broker.id}
              className="group rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-primary)]"
              onClick={() => selectBroker(broker.id)}
            >
              <Card className="h-full border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] py-0 transition group-hover:border-[var(--border-primary)] group-hover:bg-[var(--bg-primary-subtle)]">
                <CardContent className="p-5">
                  <span className="block text-lg font-semibold text-[var(--fg-base)]">{broker.name}</span>
                  {broker.accounts.length > 0 && (
                    <span className="mt-2 block text-sm text-[var(--fg-secondary)]">
                      {broker.accounts.length} account{broker.accounts.length > 1 ? 's' : ''}
                    </span>
                  )}
                </CardContent>
              </Card>
            </button>
          ))}
          {state.brokers.length === 0 && !fetching && !fetchError && (
            <Card className="border-dashed border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] py-0 sm:col-span-2 lg:col-span-3">
              <CardContent className="p-8 text-center text-sm text-[var(--fg-secondary)]">
                No DEXs found for this wallet address.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
